/* eslint-disable @typescript-eslint/triple-slash-reference */
/// <reference path="./IntlTelInputWrapper.d.ts" />
import React, { forwardRef, useCallback, useRef } from 'react';
import type { IntlTelInputRef } from 'intl-tel-input/reactWithUtils';
import type IntlTelInputType from 'intl-tel-input/reactWithUtils';
import loadable from '@loadable/component';
import 'intl-tel-input/build/css/intlTelInput.css';

export type { IntlTelInputRef };

type IntlTelInputProps = React.ComponentPropsWithoutRef<typeof IntlTelInputType>;

type Props = Readonly<
  Omit<IntlTelInputProps, 'onChangeNumber' | 'onChangeValidity' | 'initialValue'> & {
    readonly initialValue?: string | null;
    readonly onChangeNumber?: (number: string) => void;
    readonly onChangeValidity?: (valid: boolean) => void;
  }
>;

const IntlTelInputLoadable = loadable(
  () =>
    import(
      /* webpackChunkName: "static/webpack/vendor/intl-tel-input/reactWithUtils" */
      'intl-tel-input/reactWithUtils'
    ),
  {
    ssr: false,
  },
);

export const IntlTelInputWrapper = forwardRef<IntlTelInputRef, Props>(({ initialValue, onChangeNumber, onChangeValidity, ...passThroughProps }, ref) => {
  // Track last values to prevent infinite loops from IntlTelInput firing on every render
  const lastPhoneNumberRef = useRef<string | undefined>(undefined);
  const lastValidityRef = useRef<boolean | undefined>(undefined);
  const onChangeNumberRef = useRef(onChangeNumber);
  const onChangeValidityRef = useRef(onChangeValidity);

  // Keep refs fresh without re-creating callbacks
  onChangeNumberRef.current = onChangeNumber;
  onChangeValidityRef.current = onChangeValidity;

  const handleChangeNumber = useCallback((number: string) => {
    if (lastPhoneNumberRef.current !== number) {
      lastPhoneNumberRef.current = number;
      onChangeNumberRef.current?.(number);
    }
  }, []);

  const handleChangeValidity = useCallback((valid: boolean) => {
    if (lastValidityRef.current !== valid) {
      lastValidityRef.current = valid;
      onChangeValidityRef.current?.(valid);
    }
  }, []);

  return (
    <IntlTelInputLoadable
      ref={ref}
      initialValue={initialValue ?? undefined}
      onChangeNumber={handleChangeNumber}
      onChangeValidity={handleChangeValidity}
      {...passThroughProps}
    />
  );
});

IntlTelInputWrapper.displayName = 'IntlTelInputWrapper';
